import type { CreatureAttackEvent } from "@eldrun/protocol";
import { BufferAttribute, BufferGeometry, type Color, type Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { DecalBatch } from "../render/decal-batch.ts";
import { createTelegraphMaterial, telegraphTone } from "../render/shaders/telegraph.ts";
import type { CreatureTone } from "@eldrun/content";

/** O lampejo depois do impacto dura isto (ms). */
export const TELEGRAPH_FADE_MS = 300;
/** Quanto o aviso flutua acima do terreno (m). */
const LIFT = 0.06;
const RINGS = 6;

export type HeightAt = (x: number, z: number) => number;

/**
 * Leque (arco) ou disco (círculo) em coordenadas do mundo, com cada vértice na altura do terreno:
 * em encosta o aviso acompanha o chão em vez de flutuar ou se enterrar. A frente do ataque é
 * `(−sen yaw, −cos yaw)`, como nos corpos. `aRadial` vai de 0 (centro) a 1 (alcance); `aAngular`
 * de −1 a 1 entre as bordas do arco.
 */
export function telegraphGeometry(e: Pick<CreatureAttackEvent, "shape" | "x" | "z" | "yaw" | "range" | "angle"> & { width?: number; inner?: number }, height: HeightAt): BufferGeometry {
  if (e.shape === "linha") return stripGeometry(e.x, e.z, e.yaw, e.range, e.width ?? 1, height);
  if (e.shape === "circulo" && e.inner && e.inner > 0) return ringGeometry(e.x, e.z, e.inner, e.range, height);
  const span = e.shape === "circulo" ? Math.PI * 2 : (Math.min(360, Math.max(1, e.angle)) * Math.PI) / 180;
  const segments = Math.max(8, Math.round((span / (Math.PI * 2)) * 48));
  const positions: number[] = [];
  const radial: number[] = [];
  const angular: number[] = [];
  const push = (x: number, z: number, r: number, a: number) => {
    positions.push(x, height(x, z) + LIFT, z);
    radial.push(r);
    angular.push(a);
  };
  push(e.x, e.z, 0, 0);
  for (let k = 1; k <= RINGS; k++) {
    const r = (k / RINGS) * e.range;
    for (let s = 0; s <= segments; s++) {
      const u = s / segments;
      const theta = e.yaw + (u - 0.5) * span;
      push(e.x - Math.sin(theta) * r, e.z - Math.cos(theta) * r, k / RINGS, u * 2 - 1);
    }
  }
  const row = segments + 1;
  const index: number[] = [];
  const at = (k: number, s: number) => 1 + (k - 1) * row + s;
  // Anti-horário visto de cima: a face aponta para +y.
  for (let s = 0; s < segments; s++) index.push(0, at(1, s), at(1, s + 1));
  for (let k = 1; k < RINGS; k++) {
    for (let s = 0; s < segments; s++) {
      index.push(at(k, s), at(k + 1, s), at(k, s + 1), at(k, s + 1), at(k + 1, s), at(k + 1, s + 1));
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
  geometry.setAttribute("aRadial", new BufferAttribute(new Float32Array(radial), 1));
  geometry.setAttribute("aAngular", new BufferAttribute(new Float32Array(angular), 1));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}

/** Anel (chicote do Boitatá): só a coroa de `inner` a `outer`, colada ao terreno ou ao piso. */
export function ringGeometry(x: number, z: number, inner: number, outer: number, height: HeightAt): BufferGeometry {
  const segments = 48;
  const positions: number[] = [];
  const radial: number[] = [];
  const angular: number[] = [];
  for (let k = 0; k <= RINGS; k++) {
    const r = inner + (k / RINGS) * (outer - inner);
    for (let s = 0; s <= segments; s++) {
      const theta = (s / segments) * Math.PI * 2;
      const px = x - Math.sin(theta) * r;
      const pz = z - Math.cos(theta) * r;
      positions.push(px, height(px, pz) + LIFT, pz);
      radial.push(r / outer);
      angular.push((s / segments) * 2 - 1);
    }
  }
  const row = segments + 1;
  const index: number[] = [];
  for (let k = 0; k < RINGS; k++) {
    for (let s = 0; s < segments; s++) {
      const a = k * row + s;
      const b = (k + 1) * row + s;
      index.push(a, b, a + 1, a + 1, b, b + 1);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
  geometry.setAttribute("aRadial", new BufferAttribute(new Float32Array(radial), 1));
  geometry.setAttribute("aAngular", new BufferAttribute(new Float32Array(angular), 1));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Faixa (salto, carga, rastro de fogo): retângulo de `length` à frente da origem e `width` de
 * largura, com vértices na altura do terreno. `aRadial` vai de 0 a 1 ao longo; `aAngular` de −1 a
 * 1 de um lado ao outro.
 */
export function stripGeometry(x: number, z: number, yaw: number, length: number, width: number, height: HeightAt): BufferGeometry {
  const along = Math.max(2, Math.ceil(length / 1.5));
  const across = 4;
  const fx = -Math.sin(yaw);
  const fz = -Math.cos(yaw);
  const rx = Math.cos(yaw);
  const rz = -Math.sin(yaw);
  const positions: number[] = [];
  const radial: number[] = [];
  const angular: number[] = [];
  for (let i = 0; i <= along; i++) {
    for (let j = 0; j <= across; j++) {
      const a = (i / along) * length;
      const c = (j / across - 0.5) * width;
      const px = x + fx * a + rx * c;
      const pz = z + fz * a + rz * c;
      positions.push(px, height(px, pz) + LIFT, pz);
      radial.push(i / along);
      angular.push((j / across) * 2 - 1);
    }
  }
  const row = across + 1;
  const index: number[] = [];
  for (let i = 0; i < along; i++) {
    for (let j = 0; j < across; j++) {
      const a = i * row + j;
      const b = (i + 1) * row + j;
      // Anti-horário visto de cima (face para +y).
      index.push(a, a + 1, b, a + 1, b + 1, b);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
  geometry.setAttribute("aRadial", new BufferAttribute(new Float32Array(radial), 1));
  geometry.setAttribute("aAngular", new BufferAttribute(new Float32Array(angular), 1));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Andamento do aviso em `now` (ms): `progress` 0→1 até o impacto; cheio por mais `hold` ms se a
 * área segue batendo (bafo, fogo); depois, `fade` 0→1 no lampejo; `done` quando acabou.
 */
export function telegraphPhase(now: number, impactAt: number, duration: number, hold = 0): { progress: number; fade: number; done: boolean } {
  if (now < impactAt) return { progress: Math.max(0, Math.min(1, 1 - (impactAt - now) / Math.max(1, duration))), fade: 0, done: false };
  if (now < impactAt + hold) return { progress: 1, fade: 0, done: false };
  const fade = (now - impactAt - hold) / TELEGRAPH_FADE_MS;
  return { progress: 1, fade: Math.min(1, Math.max(1e-3, fade)), done: fade >= 1 };
}

interface Telegraph {
  id: string;
  /** Marca de um ataque de várias (caldeirões, raízes). */
  mark: number | undefined;
  /** A área segue perigosa por isto depois do impacto (ms). */
  hold: number;
  batch: DecalBatch;
  piece: ReturnType<DecalBatch["add"]>;
  arc: number;
  boss: number;
  color: Color;
  impactAt: number;
  duration: number;
  /** Depois disto o aviso não é mais cancelado (o caldeirão já saiu da mão: cai de qualquer jeito). */
  lockAt: number;
}

/**
 * Avisos de ataque no chão (doc 06, Telegraphs). O servidor manda o ataque com o tempo que falta
 * até o impacto (`inMs`); aqui o impacto fica em `chegada + inMs − RTT/2`, o mesmo instante em que
 * o servidor resolve o golpe contra o corpo previsto do jogador.
 */
export class Telegraphs {
  /** Redução de lampejos (Opções, F30): sem o clarão depois do impacto. */
  reduceFlash = false;
  /** Modo daltônico (F75): hachura reforçada. */
  colorblind = false;
  private readonly active: Telegraph[] = [];
  /**
   * Os avisos em lote (revisão da Fase 13): um draw por camada em vez de um por aviso. Doc 09 §2.5
   * (F75): a telegrafia fica por cima das zonas aliadas (6) e do Cerco (7), e a do chefe por cima da
   * dos inimigos.
   */
  private readonly batches: { inimigo: DecalBatch; chefe: DecalBatch };

  constructor(root: Object3D, atmosphere: AtmosphereUniforms) {
    this.batches = {
      inimigo: new DecalBatch(root, () => createTelegraphMaterial(atmosphere), 7.5),
      chefe: new DecalBatch(root, () => createTelegraphMaterial(atmosphere), 7.6),
    };
  }

  /**
   * `now` em ms; `rtt` em ms (ida e volta). `lockAfterMs`: depois deste tempo do anúncio o aviso
   * não é mais cancelado (lançamento do caldeirão); por padrão, vale até o impacto.
   */
  spawn(e: CreatureAttackEvent, now: number, rtt: number, height: HeightAt, lockAfterMs?: number, tone: CreatureTone = "inimigo"): void {
    // Um ataque novo da mesma criatura substitui o anterior que ainda podia ser cancelado; os já
    // garantidos (caldeirão no ar, marcas no chão) ficam até cair.
    this.remove((t) => t.id === e.id && now < t.lockAt && (e.mark === undefined || t.mark === undefined));
    // Bordas laterais: no arco (menos que a volta inteira) e na faixa.
    const arc = e.shape === "linha" || (e.shape === "arco" && e.angle < 360) ? 1 : 0;
    const { color, boss } = telegraphTone(tone);
    const batch = tone === "chefe" ? this.batches.chefe : this.batches.inimigo;
    const piece = batch.add(telegraphGeometry(e, height));
    batch.set(piece, 0, 0, arc, boss, color);
    const impactAt = now + Math.max(0, e.inMs - rtt / 2);
    const lockAt = lockAfterMs === undefined ? impactAt : now + Math.max(0, lockAfterMs - rtt / 2);
    this.active.push({ id: e.id, mark: e.mark, hold: e.activeMs ?? 0, batch, piece, arc, boss, color, impactAt, duration: Math.max(1, e.inMs), lockAt });
    batch.flush();
  }

  /**
   * `keep(id)` diz se o ataque daquela criatura ainda vale: falso quando ela cambaleou, morreu ou
   * sumiu antes do impacto, e o aviso sai na hora (sem anunciar um golpe que não vai acontecer).
   */
  update(now: number, keep: (id: string) => boolean = () => true): void {
    // Cancela o que ainda pode não acontecer: antes do impacto (se não travado) ou, nos golpes que
    // batem várias vezes, durante o tempo ativo (o chefe cambaleou no meio do bafo).
    this.remove((t) => ((now < t.impactAt && now < t.lockAt) || (t.hold > 0 && now < t.impactAt + t.hold)) && !keep(t.id));
    for (const t of this.active) {
      const phase = telegraphPhase(now, t.impactAt, t.duration, t.hold);
      t.batch.set(t.piece, phase.progress, phase.fade, t.arc, t.boss);
    }
    for (const batch of [this.batches.inimigo, this.batches.chefe]) {
      batch.uniform("uSemLampejo", this.reduceFlash ? 1 : 0);
      batch.uniform("uReforco", this.colorblind ? 1 : 0);
    }
    this.remove((t) => telegraphPhase(now, t.impactAt, t.duration, t.hold).done);
  }

  /** Draw calls dos avisos agora (um por camada com aviso). */
  get draws(): number {
    return this.batches.inimigo.draws + this.batches.chefe.draws;
  }

  get count(): number {
    return this.active.length;
  }

  dispose(): void {
    this.remove(() => true);
    this.batches.inimigo.dispose();
    this.batches.chefe.dispose();
  }

  private remove(match: (t: Telegraph) => boolean): void {
    let removed = false;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const t = this.active[i] as Telegraph;
      if (!match(t)) continue;
      t.batch.remove(t.piece);
      this.active.splice(i, 1);
      removed = true;
    }
    if (removed) {
      this.batches.inimigo.flush();
      this.batches.chefe.flush();
    }
  }
}
